import { isChannelId, isVideoId } from '../links/routes.ts';
import type { ClipTarget, ResolvedTarget } from '../links/resolve.ts';
import { err, ok, parseError, type ParseError, type Result } from '../result.ts';
import { asNonEmptyString, asNumber, asObject, field } from './raw.ts';

/**
 * `/api/v1/resolveurl` and `/api/v1/clips/{id}` — the two responses read here.
 *
 * Both are single records rather than lists, so R4's drop-the-item discipline does not apply: there is
 * one thing to read and failing to read it is a failure. What they have in common is that **success is
 * not a status code** — see the `unrecognised` arm below, and the zero-byte `200` the clips endpoint
 * answers with.
 */

/** The `pageType` values measured. Anything else falls to `unrecognised` with its name attached. */
const PAGE_TYPE_WATCH = 'WEB_PAGE_TYPE_WATCH';
const PAGE_TYPE_CHANNEL = 'WEB_PAGE_TYPE_CHANNEL';
const PAGE_TYPE_PLAYLIST = 'WEB_PAGE_TYPE_PLAYLIST';

export function parseResolvedUrl(raw: unknown): Result<ResolvedTarget, ParseError> {
  const source = asObject(raw);
  if (source === null) return err(parseError('resolved-url', null, 'wrong-type'));

  const pageType = asNonEmptyString(field(source, 'pageType'));
  if (pageType === null) return err(parseError('resolved-url', 'pageType', 'missing'));

  if (pageType === PAGE_TYPE_WATCH) {
    const videoId = asNonEmptyString(field(source, 'videoId'));
    if (videoId === null) return err(parseError('resolved-url', 'videoId', 'missing'));
    if (!isVideoId(videoId)) return err(parseError('resolved-url', 'videoId', 'malformed'));
    return ok({
      kind: 'video',
      videoId,
      // The endpoint does the time parsing: `?t=1h2m3s` comes back as `startTimeSeconds: 3723`.
      // Absent — not zero — when the URL carried no timestamp.
      startSec: nonNegativeSeconds(field(source, 'startTimeSeconds')),
      playlistId: asNonEmptyString(field(source, 'playlistId')),
    });
  }

  if (pageType === PAGE_TYPE_CHANNEL) {
    // `ucid` first, `browseId` as the fallback — measured identical on 5 of 5 channel resolutions, but
    // `browseId` is the field that carries `FEhashtag` and `VL<PLID>` under the other page types, so
    // the named one is the one to trust.
    const id = asNonEmptyString(field(source, 'ucid')) ?? asNonEmptyString(field(source, 'browseId'));
    if (id === null) return err(parseError('resolved-url', 'ucid', 'missing'));
    // Refused rather than passed through: a channel page type carrying something that is not a UCID is
    // a shape this build has never seen, and routing to `/channel/<whatever>` would report the
    // instance's surprise as an empty channel.
    if (!isChannelId(id)) return err(parseError('resolved-url', 'ucid', 'malformed'));
    return ok({ kind: 'channel', channelId: id });
  }

  if (pageType === PAGE_TYPE_PLAYLIST) {
    const browseId = asNonEmptyString(field(source, 'browseId'));
    if (browseId === null) return err(parseError('resolved-url', 'browseId', 'missing'));
    // `VL` is YouTube's browse prefix for a playlist — `VLPLbpi6ZahwAY7…`, and `VLRDCLAK5uy_…` for a
    // radio mix. Stripped, because `/api/v1/playlists/VLPL…` is not the same id as `PL…`.
    const playlistId = browseId.startsWith('VL') ? browseId.slice(2) : browseId;
    // `FE…` is a *feed* browse id (`FEhashtag`, `FEwhat_to_watch`). It has never appeared under this
    // page type, and if it does it is not a playlist id.
    if (playlistId === '' || playlistId.startsWith('FE')) {
      return err(parseError('resolved-url', 'browseId', 'malformed'));
    }
    return ok({ kind: 'playlist', playlistId });
  }

  return ok({ kind: 'unrecognised', pageType });
}

/**
 * `/api/v1/clips/{id}`.
 *
 * The response embeds a **whole video record** — the endpoint calls Invidious's own `get_video` — so
 * this is 40 KB for the clip measured and up to ~305 KB for a multi-format one, to learn a video id and
 * two floats. Only `video.videoId` is read: running the full video parser here would double the cost
 * of a redirect for a record the Watch page is about to fetch again anyway, and would let a video that
 * drops one field turn a working clip link into a dead one.
 */
export function parseClip(raw: unknown): Result<ClipTarget, ParseError> {
  const source = asObject(raw);
  if (source === null) return err(parseError('clip', null, 'wrong-type'));

  const video = asObject(field(source, 'video'));
  if (video === null) return err(parseError('clip', 'video', 'missing'));

  const videoId = asNonEmptyString(field(video, 'videoId'));
  if (videoId === null) return err(parseError('clip', 'video.videoId', 'missing'));
  if (!isVideoId(videoId)) return err(parseError('clip', 'video.videoId', 'malformed'));

  return ok({
    videoId,
    startSec: nonNegativeSeconds(field(source, 'startTime')),
    endSec: nonNegativeSeconds(field(source, 'endTime')),
    title: asNonEmptyString(field(source, 'clipTitle')),
  });
}

/** Fractional seconds, or `null`. A negative time is absent rather than a seek target behind zero. */
function nonNegativeSeconds(value: unknown): number | null {
  const seconds = asNumber(value);
  return seconds === null || seconds < 0 ? null : seconds;
}
